class TokenValidationService {
  constructor() {
    this.isValidating = false;
    this.validationPromise = null;
    this.validationEpoch = 0;
    this.lastValidation = null;
    this.lastValidationResult = { isValid: true };
    this.graceInterval = 30000; // 30 seconds between checks
    this.expiryWarningTime = 2 * 60 * 1000; // Warn 2 minutes before expiry
  }

  parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }

  async validateToken(getAccessTokenSilently, isAuthenticated, options = {}) {
    const forceFresh = options?.forceFresh === true;
    if (this.validationPromise) return this.validationPromise;

    const now = Date.now();
    if (!forceFresh && this.lastValidation && (now - this.lastValidation) < this.graceInterval) {
      // Even when returning cached result, recalculate timeUntilExpiry and check warning zone
      const cachedResult = { ...this.lastValidationResult };

      // If we have exp timestamp, always recalculate and check warning threshold
      if (this.lastValidationResult.exp) {
        const timeUntilExpiry = this.lastValidationResult.exp * 1000 - Date.now();
        cachedResult.timeUntilExpiry = timeUntilExpiry;

        // Check if token expired since last validation
        if (timeUntilExpiry <= 0) {
          console.log('[TokenValidationService] Token expired since last check, running fresh validation');
          // Fall through to fresh validation
        }
        // Check if we've NOW entered the warning zone (< 2 minutes)
        else if (timeUntilExpiry < this.expiryWarningTime && timeUntilExpiry > 0) {
          cachedResult.warningOnly = true;
          cachedResult.isValid = true;
          cachedResult.requiresLogin = false;
          cachedResult.error = 'Your session will expire soon';

          console.log('[TokenValidationService] CACHED result NOW in warning zone:', {
            timeUntilExpiry,
            warningOnly: true,
            timeSinceLastValidation: now - this.lastValidation,
          });

          return cachedResult;
        }
        // Token still has > 2 minutes, clear warning if it was set
        else {
          cachedResult.warningOnly = false;
          cachedResult.requiresLogin = false;
          cachedResult.isValid = true;

          console.log('[TokenValidationService] Returning CACHED result (updated, no warning):', {
            timeUntilExpiry,
            timeSinceLastValidation: now - this.lastValidation,
          });

          return cachedResult;
        }
      } else {
        console.log('[TokenValidationService] Returning CACHED result (no exp available):', {
          isValid: this.lastValidationResult.isValid,
          timeSinceLastValidation: now - this.lastValidation,
        });
        return this.lastValidationResult;
      }
    }

    if (!isAuthenticated) {
      return {
        isValid: false,
        error: 'Account is not authenticated',
        requiresLogin: true
      };
    }

    const validationEpochAtStart = this.validationEpoch;
    const rememberResult = (result) => {
      if (validationEpochAtStart === this.validationEpoch) {
        this.lastValidation = Date.now();
        this.lastValidationResult = result;
      }
      return result;
    };

    this.validationPromise = (async () => {
      this.isValidating = true;
      console.log('[TokenValidationService] Running FRESH validation');
      try {
        if (!forceFresh) {
          // First try with cache to check expiry
          const token = await getAccessTokenSilently({
            cacheMode: 'cache-only',
            timeoutInSeconds: 5
          });

          // Check if token is about to expire
          const payload = this.parseJwt(token);
          if (payload && payload.exp) {
            const expiryTime = payload.exp * 1000;
            const timeUntilExpiry = expiryTime - Date.now();

            if (timeUntilExpiry <= 0) {
              return rememberResult({
                isValid: false,
                error: 'Your session has expired',
                requiresLogin: true,
                timeUntilExpiry,
                exp: payload.exp
              });
            }

            // If token expires in less than 2 minutes, show warning
            if (timeUntilExpiry < this.expiryWarningTime && timeUntilExpiry > 0) {
              return rememberResult({
                isValid: true,
                error: 'Your session will expire soon',
                requiresLogin: false,
                warningOnly: true,
                timeUntilExpiry,
                exp: payload.exp  // Store exp for recalculation
              });
            }

            return rememberResult({
              isValid: true,
              requiresLogin: false,
              warningOnly: false,
              timeUntilExpiry,
              exp: payload.exp,
            });
          }
        }

        try {
          // Force network token acquisition when requested.
          const token = await getAccessTokenSilently({
            cacheMode: forceFresh ? 'off' : undefined,
            ignoreCache: forceFresh ? true : undefined,
            timeoutInSeconds: 10
          });

          // Check expiry on fresh token too
          const payload = this.parseJwt(token);
          if (payload && payload.exp) {
            const expiryTime = payload.exp * 1000;
            const timeUntilExpiry = expiryTime - Date.now();

            if (timeUntilExpiry <= 0) {
              return rememberResult({
                isValid: false,
                error: 'Your session has expired',
                requiresLogin: true,
                timeUntilExpiry,
                exp: payload.exp
              });
            }

            if (timeUntilExpiry < this.expiryWarningTime && timeUntilExpiry > 0) {
              return rememberResult({
                isValid: true,
                error: 'Your session will expire soon',
                requiresLogin: false,
                warningOnly: true,
                timeUntilExpiry,
                exp: payload.exp  // Store exp for recalculation
              });
            }

            return rememberResult({
              isValid: true,
              requiresLogin: false,
              warningOnly: false,
              timeUntilExpiry,
              exp: payload.exp,
            });
          }

          return rememberResult({ isValid: true });
        } catch (error) {
          if (error.message?.includes('Missing Refresh Token') ||
              error.message?.includes('not authenticated') ||
              error.error === 'login_required' ||
              error.error === 'consent_required' ||
              error.error === 'invalid_grant') {
            return rememberResult({
              isValid: false,
              error: 'Your session has expired',
              requiresLogin: true
            });
          }

          return rememberResult({
            isValid: false,
            error: error?.message || 'Unable to validate session',
            requiresLogin: false
          });
        }
      } finally {
        if (validationEpochAtStart === this.validationEpoch) {
          this.isValidating = false;
        }
      }
    })();

    const currentValidationPromise = this.validationPromise;

    try {
      return await currentValidationPromise;
    } finally {
      if (this.validationPromise === currentValidationPromise) {
        this.validationPromise = null;
      }
    }
  }

  resetValidation() {
    this.validationEpoch += 1;
    this.validationPromise = null;
    this.isValidating = false;
    this.lastValidation = null;
    this.lastValidationResult = { isValid: true };
  }
}

export default new TokenValidationService();
